import React, { useState, useRef, useMemo } from "react"
import { useStaticQuery, graphql } from "gatsby"
import ContactForm from "components/molecules/ContactForm"
import PropTypes from "prop-types"
import { useIntl } from "gatsby-plugin-intl"

import postForm from "utils/postForm"
import randomize from "utils/randomize"
import getMediaUrl from "utils/getMediaUrl"
import trackCustomEventTrigger from "utils/trackCustomEventTrigger"
import trackingCategoriesHelper from "utils/trackingCategoriesHelper"

import H from "components/atoms/H"
import P from "components/atoms/P"
import BtnLink from "components/atoms/BtnLink"

import Send from "assets/icons/send.svg"
import Phone from "assets/icons/phone.svg"
import {
  StyledContact,
  StyledContactWrapper,
  StyledFormWrapper,
  StyledTextWrapper,
  titleStyles,
  successTitleStyles,
  StyledImg,
  StyledBackground,
  StyledSuccessWrapper,
  StyledCloseButton,
  StyledCloseIcon,
  StyledSeparator,
  StyledImageWrapper,
  StyledContactInfoWrapper,
  StyledContactIconWrapper,
  contactInfoStyles,
} from "./ContactModal.styles"

const ContactModal = ({ handleCancel, asPage }) => {
  const {
    api: { contactModalImages },
  } = useStaticQuery(graphql`
    query ContactModalQuery {
      api {
        contactModalImages {
          image
          id
        }
      }
    }
  `)
  const { formatMessage } = useIntl()
  const [responseStatus, setResponseStatus] = useState(false)
  const [phoneError, setPhoneError] = useState(null)
  const inputEl = useRef(null)

  const sendForm = async (formData) => {
    const response = await postForm(formData)

    setResponseStatus(
      response.data.sendContactMessage && response.data.sendContactMessage.ok
    )

    if (inputEl.current && response.data.sendContactMessage.ok) {
      inputEl.current.scrollIntoView({ behavior: "smooth" })
    }

    const { errors } = response
    setPhoneError(
      errors && errors[0].extensions.validationErrors[0].phoneNumber
    )
    trackCustomEventTrigger({
      category: trackingCategoriesHelper.CONTACT,
      action: "Click",
      label: `${trackingCategoriesHelper.CONTACT} button(Send message)`,
    })
  }

  return useMemo(() => {
    const getImage = randomize(contactModalImages)
    return (
      <StyledContact asPage={asPage}>
        <StyledContactWrapper id="contact" ref={inputEl} data-testid="contact">
          {handleCancel && (
            <StyledCloseButton
              onClick={handleCancel}
              data-testid="contactCloseButton"
            >
              <StyledCloseIcon />
            </StyledCloseButton>
          )}
          {responseStatus ? (
            <StyledSuccessWrapper data-testid="contactSuccessWrapper">
              <H
                as={asPage ? "h1" : "h2"}
                extraStyles={successTitleStyles}
                data-testid="contactSuccessTitle"
              >
                {formatMessage({ id: "contactForm.success.title" })}
              </H>
              <P data-testid="contactSuccessParagraph">
                {formatMessage({ id: "contactForm.success.message" })}
              </P>
            </StyledSuccessWrapper>
          ) : (
            <>
              <StyledTextWrapper data-testid="contactTextWrapper">
                <H
                  as={asPage ? "h1" : "h2"}
                  extraStyles={titleStyles}
                  data-testid="contactTextTitle"
                >
                  {formatMessage({ id: "contactForm.title" })}
                </H>
                {asPage ? (
                  <StyledContactInfoWrapper data-testid="contactInfoWrapper">
                    <BtnLink
                      to="mailto:info@exdev.com"
                      extraStyles={contactInfoStyles}
                      data-testid="contactInfoEmail"
                      trackingOptions={{
                        category: trackingCategoriesHelper.CONTACT,
                        action: "Click",
                        label: `${trackingCategoriesHelper.CONTACT} link(Email)`,
                      }}
                    >
                      <StyledContactIconWrapper>
                        <Send />
                      </StyledContactIconWrapper>
                      <span>info@exdev.com</span>
                    </BtnLink>
                    <BtnLink
                      to="tel:+16263845050"
                      extraStyles={contactInfoStyles}
                      data-testid="contactInfoPhone"
                      trackingOptions={{
                        category: trackingCategoriesHelper.CONTACT,
                        action: "Click",
                        label: `${trackingCategoriesHelper.CONTACT} link(Phone)`,
                      }}
                    >
                      <StyledContactIconWrapper>
                        <Phone />
                      </StyledContactIconWrapper>
                      <span>626-384-5050</span>
                    </BtnLink>
                  </StyledContactInfoWrapper>
                ) : (
                  <StyledImageWrapper>
                    {!!getImage.length && (
                      <>
                        <StyledImg
                          data-testid="contactTextImage"
                          src={getMediaUrl(getImage[0].image)}
                        />
                        <StyledBackground />
                      </>
                    )}
                  </StyledImageWrapper>
                )}
              </StyledTextWrapper>
              {!asPage && (
                <>
                  <StyledSeparator />
                  <StyledFormWrapper>
                    <ContactForm
                      sendForm={sendForm}
                      phoneError={phoneError}
                      type="secondary"
                      onCancel={handleCancel}
                      asPage={asPage}
                    />
                  </StyledFormWrapper>
                </>
              )}
            </>
          )}
        </StyledContactWrapper>
      </StyledContact>
    )
  }, [responseStatus])
}

ContactModal.propTypes = {
  handleCancel: PropTypes.func,
  asPage: PropTypes.bool,
}

ContactModal.defaultProps = {
  handleCancel: null,
  asPage: false,
}
export default ContactModal
